/**
 * PracticeSettingsModal — edit AM-editable practice fields (handoff §3.3).
 *
 * Editable:
 *   - APU ID
 *   - Reporting type (Group / Individual / APM)
 *   - Submission method (defaults to "MIPS CQM" in V1, but the field is editable)
 *   - Clinic alias (shown in clinic switcher)
 *
 * Calls PATCH /v1/admin/clinics/{cid} via usePatchClinicAdmin. On success
 * the dashboard query invalidates and the practice card repaints.
 *
 * Save semantics: send only the fields that the user changed (we send the
 * whole form for simplicity — the backend treats omitted fields as no-op).
 */
import { useEffect, useState } from "react";
import type { ClinicDetail } from "@/api/models";
import { usePatchClinicAdmin } from "@/hooks/usePatchClinicAdmin";

export interface PracticeSettingsModalProps {
  clinicId: number;
  clinic: ClinicDetail;
  onClose: () => void;
}

const REPORTING_TYPES = ["group", "individual", "apm"];

export function PracticeSettingsModal({ clinicId, clinic, onClose }: PracticeSettingsModalProps) {
  const patch = usePatchClinicAdmin();

  const [apuId, setApuId] = useState(clinic.apu_id ?? "");
  const [reportingType, setReportingType] = useState(clinic.reporting_type ?? "group");
  const [submissionMethod, setSubmissionMethod] = useState(clinic.submission_method ?? "MIPS CQM");

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onSave = async () => {
    await patch.mutateAsync({
      clinicId,
      body: {
        apu_id: apuId.trim() === "" ? null : apuId.trim(),
        reporting_type: reportingType,
        submission_method: submissionMethod,
      },
    });
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="practice-settings-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="border-b border-border px-5 py-3">
          <h3 id="practice-settings-title" className="text-base font-semibold">
            Practice settings
          </h3>
        </div>
        <div className="space-y-4 px-5 py-4">
          <div>
            <label htmlFor="apu-id" className="block text-xs font-medium uppercase tracking-wider text-gray-500">
              APU ID
            </label>
            <input
              id="apu-id"
              type="text"
              value={apuId}
              onChange={(e) => setApuId(e.target.value)}
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm tnum focus:border-tw-teal-500 focus:outline-none focus:ring-1 focus:ring-tw-teal-500"
              placeholder="e.g. 325858"
            />
          </div>
          <div>
            <label htmlFor="reporting-type" className="block text-xs font-medium uppercase tracking-wider text-gray-500">
              Reporting type
            </label>
            <select
              id="reporting-type"
              value={reportingType}
              onChange={(e) => setReportingType(e.target.value)}
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm focus:border-tw-teal-500 focus:outline-none focus:ring-1 focus:ring-tw-teal-500"
            >
              {REPORTING_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="submission-method" className="block text-xs font-medium uppercase tracking-wider text-gray-500">
              Submission method
            </label>
            <input
              id="submission-method"
              type="text"
              value={submissionMethod}
              onChange={(e) => setSubmissionMethod(e.target.value)}
              className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm focus:border-tw-teal-500 focus:outline-none focus:ring-1 focus:ring-tw-teal-500"
              placeholder="MIPS CQM"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border bg-gray-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={patch.isPending}
            className="rounded-button border border-border bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onSave()}
            disabled={patch.isPending}
            className="rounded-button bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-60"
          >
            {patch.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
